(function () {
  const MOBILE_QUERY = '(max-width: 992px)';
  const DESKTOP_HOVER_QUERY = '(min-width: 993px) and (hover: hover) and (pointer: fine)';
  const PANEL_TRANSITION_MS = 300;
  const HOVER_SWITCH_MS = 80;
  const TAB_KEY_PARAM = 'tabkey';
  let lastPointerType = 'mouse';
  let lastViewportWidth = window.innerWidth;

  function isMobile() {
    return window.matchMedia(MOBILE_QUERY).matches;
  }

  function canDesktopHoverSwitch() {
    return window.matchMedia(DESKTOP_HOVER_QUERY).matches;
  }

  function isMousePointer(pointerType) {
    return (pointerType || lastPointerType) === 'mouse';
  }

  function canUseDesktopHover(pointerType) {
    return canDesktopHoverSwitch() && isMousePointer(pointerType);
  }

  function getTabFromTarget(el) {
    if (!el || !el.closest) return null;

    const cta = el.closest('[data-goal-cta]');
    if (cta) {
      const tabId = cta.getAttribute('data-goal-cta');
      const section = getSectionRoot(cta);
      return tabId && section
        ? section.querySelector(`[data-goal-tab="${CSS.escape(tabId)}"]`)
        : null;
    }

    const tab = el.closest('[data-goal-tab]');
    if (tab) return tab;

    const card = el.closest('[data-goal-card]');
    return card ? card.querySelector('[data-goal-tab]') : null;
  }

  function commitGoalCta(section, tab) {
    if (!section || !tab) return;

    clearHoverTimer(section);
    activateGoalTab(section, tab.dataset.goalTab, { animatePanel: true });
    setUrlTabKey(tab.getAttribute('data-tab-key'));
    maybeScrollToForm(section, tab);
  }

  function clearHoverTimer(section) {
    if (!section || !section._hoverTimer) return;
    clearTimeout(section._hoverTimer);
    section._hoverTimer = null;
  }

  function previewGoalTab(section, tab) {
    if (!section || !tab) return;
    if (tab.classList.contains('is-active')) return;

    activateGoalTab(section, tab.dataset.goalTab, {
      skipScroll: true,
      animatePanel: false,
    });
  }

  function prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function getSectionRoot(el) {
    return el.closest('[data-collaboration-goals]');
  }

  function getTabs(section) {
    return Array.from(section.querySelectorAll('[data-goal-tab]'));
  }

  function getPanels(section) {
    return Array.from(section.querySelectorAll('[data-goal-panel]'));
  }

  function getPanelsWrap(section) {
    return section.querySelector('.collaboration-goals__panels');
  }

  function getTabsSwiperEl(section) {
    return section.querySelector('[data-goal-tabs-swiper]');
  }

  function getTabIndex(section, tabId) {
    return getTabs(section).findIndex((tab) => tab.dataset.goalTab === tabId);
  }

  function equalizeTabButtonWidths(section) {
    const buttons = section.querySelectorAll('.collaboration-goals__tab-button-text');
    buttons.forEach((button) => {
      button.style.width = '';
    });

    const width = Math.max(0, ...Array.from(buttons, (button) => button.offsetWidth));
    if (!width) return;

    buttons.forEach((button) => {
      button.style.width = `${width}px`;
    });
  }

  function getPartnershipFormContainer() {
    return document.querySelector('[data-partnership-form-host]');
  }

  function getHeaderOffset() {
    const announceBar = document.querySelector('.announcement-bar-section');
    const header = document.querySelector('sticky-header') || document.querySelector('.section-header');
    const announceHeight = announceBar ? announceBar.clientHeight : 0;
    const headerHeight = header ? header.clientHeight : 0;
    return announceHeight + headerHeight + 10;
  }

  function isDesignMode() {
    return Boolean(window.Shopify && window.Shopify.designMode);
  }

  function getUrlTabKey() {
    const params = new URLSearchParams(window.location.search);

    for (const [key, value] of params.entries()) {
      if (key.toLowerCase() === TAB_KEY_PARAM && value.trim()) {
        return value.trim();
      }
    }

    return '';
  }

  function setUrlTabKey(tabKey) {
    if (!tabKey || isDesignMode()) return;
    if (!window.history || typeof window.history.replaceState !== 'function') return;

    try {
      const url = new URL(window.location.href);
      const params = url.searchParams;
      const existingNames = [];

      for (const [name] of params.entries()) {
        if (name.toLowerCase() === TAB_KEY_PARAM) existingNames.push(name);
      }

      const current = existingNames.length ? params.get(existingNames[0]) : '';
      if (current === tabKey && existingNames.length === 1 && existingNames[0] === TAB_KEY_PARAM) {
        return;
      }

      existingNames.forEach((name) => params.delete(name));
      params.set(TAB_KEY_PARAM, tabKey);

      window.history.replaceState(window.history.state, '', `${url.pathname}${url.search}${url.hash}`);
    } catch {
      // Keep tab switching / scroll working if the URL cannot be updated.
    }
  }

  function findTabByTabKey(section, tabKey) {
    if (!section || !tabKey) return null;

    const needle = tabKey.toLowerCase();
    return (
      getTabs(section).find((tab) => {
        const key = (tab.getAttribute('data-tab-key') || '').trim().toLowerCase();
        return key === needle;
      }) || null
    );
  }

  function canScrollToForm(section, tab) {
    if (!section || !tab) return false;
    if (section.dataset.enableScrollToForm !== 'true') return false;
    return Boolean(tab.getAttribute('data-airtable-key'));
  }

  function scrollToMatchingForm(airtableKey) {
    if (!airtableKey) return;

    const container = getPartnershipFormContainer();
    if (!container) return;

    const matchedForm = container.querySelector(
      `[data-airtable-form][data-airtable-key="${CSS.escape(airtableKey)}"]`
    );
    if (!matchedForm) return;

    const target =
      matchedForm.closest('.collaboration-partnership-form') || matchedForm;

    const top = target.getBoundingClientRect().top + window.pageYOffset - getHeaderOffset();
    window.scrollTo({
      top: Math.max(top, 0),
      behavior: prefersReducedMotion() ? 'auto' : 'smooth',
    });
  }

  function maybeScrollToForm(section, tab) {
    if (!canScrollToForm(section, tab)) return;
    scrollToMatchingForm(tab.getAttribute('data-airtable-key'));
  }

  function scrollToUrlMatchedForm(tab) {
    if (!tab || isDesignMode()) return;

    const run = () => {
      scrollToMatchingForm(tab.getAttribute('data-airtable-key'));
    };

    window.requestAnimationFrame(() => {
      window.setTimeout(run, 80);
    });
  }

  function setActiveAirtableForm(airtableKey) {
    const container = getPartnershipFormContainer();
    if (!container) return;

    const forms = Array.from(container.querySelectorAll('[data-airtable-form]'));
    if (!forms.length) return;

    let didMatch = false;
    let matchedOnce = false;
    forms.forEach((form) => {
      const matches = form.dataset.airtableKey === airtableKey;

      if (matches && !matchedOnce) {
        didMatch = true;
        matchedOnce = true;
        form.toggleAttribute('hidden', false);
        return;
      }

      form.toggleAttribute('hidden', true);
    });

    if (!didMatch) {
      forms.forEach((form, index) => {
        form.toggleAttribute('hidden', index !== 0);
      });
    }
  }

  function syncPartnershipForm(section, tabId) {
    if (!section || !tabId) return;

    const tab = section.querySelector(`[data-goal-tab="${CSS.escape(tabId)}"]`);
    if (!tab) return;

    setActiveAirtableForm(tab.dataset.airtableKey || 'a');
  }

  function clearPanelTransition(section) {
    if (section._panelTransitionTimer) {
      clearTimeout(section._panelTransitionTimer);
      section._panelTransitionTimer = null;
    }
    section.dataset.panelTransitioning = 'false';
  }

  function setActivePanelInstant(section, tabId) {
    clearPanelTransition(section);

    const panelsWrap = getPanelsWrap(section);
    if (panelsWrap) panelsWrap.style.minHeight = '';

    getPanels(section).forEach((panel) => {
      const isActive = panel.dataset.goalPanel === tabId;
      panel.classList.remove(
        'collaboration-goals__panel--leaving',
        'collaboration-goals__panel--entering',
        'collaboration-goals__panel--active'
      );
      panel.classList.toggle('is-active', isActive);
      panel.toggleAttribute('hidden', !isActive);
      panel.setAttribute('aria-hidden', isActive ? 'false' : 'true');
    });
  }

  function setActivePanel(section, tabId, options) {
    const opts = options || {};
    const animate = opts.animate !== false;
    const panels = getPanels(section);
    const panelsWrap = getPanelsWrap(section);
    const next = panels.find((panel) => panel.dataset.goalPanel === tabId);

    if (!next || !panelsWrap) return;

    const current = panels.find(
      (panel) => panel.classList.contains('is-active') && !panel.hasAttribute('hidden')
    );

    if (current === next) return;

    if (!isMobile() || !animate || prefersReducedMotion()) {
      setActivePanelInstant(section, tabId);
      return;
    }

    clearPanelTransition(section);
    section.dataset.panelTransitioning = 'true';

    const fromHeight = panelsWrap.offsetHeight;
    panelsWrap.style.minHeight = `${fromHeight}px`;

    panels.forEach((panel) => {
      if (panel === current || panel === next) return;
      panel.classList.remove(
        'collaboration-goals__panel--leaving',
        'collaboration-goals__panel--entering',
        'collaboration-goals__panel--active'
      );
      panel.classList.remove('is-active');
      panel.setAttribute('hidden', '');
      panel.setAttribute('aria-hidden', 'true');
    });

    next.removeAttribute('hidden');
    next.classList.add('is-active', 'collaboration-goals__panel--entering');
    next.setAttribute('aria-hidden', 'false');

    if (current) {
      current.classList.add('collaboration-goals__panel--leaving');
    }

    const toHeight = panelsWrap.offsetHeight;

    requestAnimationFrame(() => {
      if (current) {
        current.classList.add('collaboration-goals__panel--leaving');
      }

      next.classList.add('collaboration-goals__panel--active');
      next.classList.remove('collaboration-goals__panel--entering');
      panelsWrap.style.minHeight = `${toHeight}px`;

      section._panelTransitionTimer = setTimeout(() => {
        panels.forEach((panel) => {
          const isActive = panel.dataset.goalPanel === tabId;
          panel.classList.remove(
            'collaboration-goals__panel--leaving',
            'collaboration-goals__panel--entering',
            'collaboration-goals__panel--active'
          );
          panel.classList.toggle('is-active', isActive);
          panel.toggleAttribute('hidden', !isActive);
          panel.setAttribute('aria-hidden', isActive ? 'false' : 'true');
        });

        panelsWrap.style.minHeight = '';
        section.dataset.panelTransitioning = 'false';
        section._panelTransitionTimer = null;
      }, PANEL_TRANSITION_MS);
    });
  }

  function updateNavState(section) {
    const prevBtn = section.querySelector('[data-goal-nav="prev"]');
    const nextBtn = section.querySelector('[data-goal-nav="next"]');
    const swiper = section._goalsSwiper;

    if (swiper && isMobile()) {
      if (prevBtn) prevBtn.disabled = swiper.isBeginning;
      if (nextBtn) nextBtn.disabled = swiper.isEnd;
      return;
    }

    const tabs = getTabs(section);
    const activeIndex = tabs.findIndex((tab) => tab.classList.contains('is-active'));

    if (prevBtn) prevBtn.disabled = activeIndex <= 0;
    if (nextBtn) nextBtn.disabled = activeIndex >= tabs.length - 1;
  }

  function setActiveTabs(section, tabId) {
    getTabs(section).forEach((tab) => {
      const isActive = tab.dataset.goalTab === tabId;
      tab.classList.toggle('is-active', isActive);
      tab.setAttribute('aria-selected', isActive ? 'true' : 'false');

      const card = tab.closest('[data-goal-card]');
      if (card) card.classList.toggle('is-active', isActive);
    });
    updateNavState(section);
  }

  function slideToTab(section, tabId, speed) {
    const swiper = section._goalsSwiper;
    if (!swiper || !isMobile()) return;

    const index = getTabIndex(section, tabId);
    if (index < 0 || swiper.activeIndex === index) return;

    section._goalSwiperLock = true;
    swiper.slideTo(index, speed === undefined ? 400 : speed);
    window.setTimeout(() => {
      section._goalSwiperLock = false;
    }, 450);
  }

  function activateGoalTab(section, tabId, options) {
    if (!section || !tabId) return;

    const opts = Object.assign(
      {
        updateTabs: true,
        updatePanel: true,
        skipScroll: false,
        speed: 400,
        animatePanel: true,
      },
      options || {}
    );

    if (opts.updateTabs) {
      setActiveTabs(section, tabId);
    }

    if (opts.updatePanel) {
      setActivePanel(section, tabId, { animate: opts.animatePanel });
      syncPartnershipForm(section, tabId);
    }

    if (!opts.skipScroll && isMobile()) {
      slideToTab(section, tabId, opts.speed);
    }
  }

  function destroyTabsSwiper(section) {
    if (!section._goalsSwiper) return;

    section._goalsSwiper.destroy(true, true);
    section._goalsSwiper = null;
    section._goalSwiperLock = false;
  }

  function resetDesktopTabs(section) {
    if (isMobile()) return;

    destroyTabsSwiper(section);

    const el = getTabsSwiperEl(section);
    if (!el) return;

    el.classList.remove(
      'swiper-initialized',
      'swiper-horizontal',
      'swiper-pointer-events',
      'swiper-backface-hidden',
      'swiper-android'
    );
    el.removeAttribute('style');

    const wrapper = el.querySelector('.swiper-wrapper');
    if (wrapper) wrapper.removeAttribute('style');

    el.querySelectorAll('.swiper-slide').forEach((slide) => {
      slide.removeAttribute('style');
    });
  }

  function whenSwiperReady(callback) {
    if (typeof Swiper !== 'undefined') {
      callback();
      return;
    }

    let attempts = 0;
    const timer = window.setInterval(() => {
      attempts += 1;
      if (typeof Swiper !== 'undefined') {
        window.clearInterval(timer);
        callback();
        return;
      }
      if (attempts >= 100) {
        window.clearInterval(timer);
      }
    }, 50);
  }

  function createTabsSwiper(section) {
    const el = getTabsSwiperEl(section);
    if (!el || section._goalsSwiper) return;

    section._goalsSwiper = new Swiper(el, {
      slidesPerView: 'auto',
      spaceBetween: 30,
      speed: 400,
      watchOverflow: true,
      navigation: {
        prevEl: section.querySelector('[data-goal-nav="prev"]'),
        nextEl: section.querySelector('[data-goal-nav="next"]'),
      },
      on: {
        slideChange(swiper) {
          if (section._goalSwiperLock) return;

          const tab = swiper.slides[swiper.activeIndex]?.querySelector('[data-goal-tab]');
          if (!tab) return;

          activateGoalTab(section, tab.dataset.goalTab, {
            skipScroll: true,
            updateTabs: true,
            animatePanel: true,
          });
        },
        reachBeginning() {
          updateNavState(section);
        },
        reachEnd() {
          updateNavState(section);
        },
        fromEdge() {
          updateNavState(section);
        },
      },
    });

    updateNavState(section);
  }

  function initTabsSwiper(section) {
    destroyTabsSwiper(section);

    if (!isMobile()) return;

    whenSwiperReady(() => {
      if (!isMobile()) return;
      createTabsSwiper(section);

      const tabs = getTabs(section);
      const activeTab = tabs.find((tab) => tab.classList.contains('is-active')) || tabs[0];
      if (!activeTab || !section._goalsSwiper) return;

      const activeIndex = getTabIndex(section, activeTab.dataset.goalTab);
      if (activeIndex >= 0) {
        section._goalSwiperLock = true;
        section._goalsSwiper.slideTo(activeIndex, 0);
        window.setTimeout(() => {
          section._goalSwiperLock = false;
        }, 0);
      }
    });
  }

  function initSection(section) {
    const tabs = getTabs(section);
    if (!tabs.length) return;

    const urlTab = findTabByTabKey(section, getUrlTabKey());
    const activeTab = urlTab || tabs.find((tab) => tab.classList.contains('is-active')) || tabs[0];

    activateGoalTab(section, activeTab.dataset.goalTab, {
      skipScroll: true,
      speed: 0,
      animatePanel: false,
    });

    resetDesktopTabs(section);
    initTabsSwiper(section);
    equalizeTabButtonWidths(section);

    if (urlTab) {
      scrollToUrlMatchedForm(urlTab);
    }
  }

  function initAll() {
    document.querySelectorAll('[data-collaboration-goals]').forEach(initSection);
  }

  document.addEventListener(
    'pointerdown',
    (e) => {
      lastPointerType = e.pointerType || 'mouse';
    },
    true
  );

  document.addEventListener('pointerover', (e) => {
    if (!canUseDesktopHover(e.pointerType)) return;

    const card = e.target.closest?.('[data-goal-card]');
    if (!card) return;

    const related = e.relatedTarget;
    if (related && card.contains(related)) return;

    const section = getSectionRoot(card);
    const tab = card.querySelector('[data-goal-tab]');
    if (!section || !tab) return;

    clearHoverTimer(section);
    if (tab.classList.contains('is-active')) return;

    section._hoverTimer = window.setTimeout(() => {
      section._hoverTimer = null;
      if (!canUseDesktopHover() || !tab.isConnected) return;
      previewGoalTab(section, tab);
    }, HOVER_SWITCH_MS);
  });

  document.addEventListener('pointerout', (e) => {
    if (!canUseDesktopHover(e.pointerType)) return;

    const card = e.target.closest?.('[data-goal-card]');
    if (!card) return;

    const related = e.relatedTarget;
    if (related && card.contains(related)) return;

    clearHoverTimer(getSectionRoot(card));
  });

  document.addEventListener('focusin', (e) => {
    const tab = getTabFromTarget(e.target);
    if (!tab) return;
    if (!e.target.closest('[data-goal-tab]') && !e.target.closest('[data-goal-cta]')) return;

    const section = getSectionRoot(tab);
    if (!section) return;

    previewGoalTab(section, tab);
  });

  document.addEventListener('click', (e) => {
    const cta = e.target.closest?.('[data-goal-cta]');
    if (cta) {
      e.preventDefault();
      const section = getSectionRoot(cta);
      const tab = getTabFromTarget(cta);
      commitGoalCta(section, tab);
      return;
    }

    const tab = e.target.closest?.('[data-goal-tab]');
    if (!tab) return;

    e.preventDefault();
    const section = getSectionRoot(tab);
    if (!section) return;

    clearHoverTimer(section);
    activateGoalTab(section, tab.dataset.goalTab, { animatePanel: true });
  });

  document.addEventListener('keydown', (e) => {
    if (e.key !== 'Enter' && e.key !== ' ') return;

    const cta = e.target.closest?.('[data-goal-cta]');
    if (!cta || cta !== e.target) return;

    e.preventDefault();
    const section = getSectionRoot(cta);
    const tab = getTabFromTarget(cta);
    commitGoalCta(section, tab);
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', (e) => {
    const section = e.target.querySelector('[data-collaboration-goals]');
    if (section) initSection(section);
  });

  window.matchMedia(MOBILE_QUERY).addEventListener('change', () => {
    document.querySelectorAll('[data-collaboration-goals]').forEach((section) => {
      if (isMobile()) {
        destroyTabsSwiper(section);
        initTabsSwiper(section);
      } else {
        resetDesktopTabs(section);
      }
      updateNavState(section);
    });
  });

  window.addEventListener('resize', () => {
    const nextWidth = window.innerWidth;
    if (nextWidth === lastViewportWidth) return;

    lastViewportWidth = nextWidth;
    document.querySelectorAll('[data-collaboration-goals]').forEach(equalizeTabButtonWidths);
  });
})();
